/* eslint-disable no-underscore-dangle */
/* eslint-disable arrow-body-style */
/* eslint-disable quote-props */
/* eslint-disable object-shorthand */
/* eslint-disable @typescript-eslint/naming-convention */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap, switchMap } from 'rxjs/operators';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpResponseAPI } from '../models/httpResponse';
import { Storage } from '@ionic/storage-angular';
import { concatMap } from 'rxjs/operators';

const TOKEN_KEY = 'auth-token';
const USERNAME_TOKEN_KEY = 'auth-username';
const TYPE_USER_KEY = 'type-user';  // Pueden haber dos tipos de usuario: 1 = General, 2 = Empleado

const LOGIN_ENDPOINT = '/auth/token';
const ACTIVITIES_ENDPOINT = '/activity';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    // #region Propiedades públicas
    isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
    userType: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    token = '';

    private _storage: Storage | null = null;
    // #endregion

    // #region Propiedades privadas
    private apiUrl: string = environment.apiUrl;
    // #endregion

    constructor(
        private http: HttpClient,
        private storage: Storage
    ) {
        // Inicialización del storage
        this.init();
    }

    async init() {
        const storage = await this.storage.create();
        this._storage = storage;

        // Carga del token y tipo de usuario
        await this.loadToken();
        await this.loadUserType();
    }

    private async loadUserType() {
        const typeUser = await this._storage.get(TYPE_USER_KEY);
        this.userType.next(typeUser || '');
    }

    /**
     * Comprueba si el usuario está autenticado.
     *
     * @returns Un Observable que emite un valor booleano indicando si el usuario está autenticado.
     */
    usuarioAutenticado(): Observable<boolean> {

        if (this._storage == null) {

            return new Observable<boolean>((observer) => {
                const promesa = from(this.storage.create());

                promesa
                    .pipe(
                        concatMap((storage) => {
                            this._storage = storage;

                            return this.comprobarUsuarioAutenticado();
                        })
                    )
                    .subscribe({
                        next: (resultado: boolean) => {
                            observer.next(resultado);
                        }
                    });
            });
        } else {
            return this.comprobarUsuarioAutenticado();
        }

    }

    /**
     * Comprueba si el usuario está autenticado.
     * @returns Un Observable que emite un valor booleano indicando si el usuario está autenticado.
     */
    private comprobarUsuarioAutenticado(): Observable<boolean> {
        const promesa = from(this._storage.get(TOKEN_KEY));

        return new Observable<boolean>((observer) => {
            promesa
                .subscribe({
                    next: (resultado: string) => {
                        observer.next(resultado && resultado !== '');
                    },
                    complete: () => {
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Loads the authentication token from storage and updates the authentication state.
     */
    public async loadToken() {
        const token = await this._storage.get(TOKEN_KEY);
        if (token && token !== '') {
            this.token = token;
            this.isAuthenticated.next(true);

            console.log('Token cargado', token);
        } else {
            this.isAuthenticated.next(false);
        }
    }

    /**
     * Logs in the user with the provided credentials.
     *
     * @param credentials - The user's email and password.
     * @returns An Observable that emits the access token upon successful login.
     */
    public login(credentials: { email: string; password: string }): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
        const loginFormBody = `username=${credentials.email}&password=${credentials.password}&grant_type=password`;
        return this.http.post(this.apiUrl + LOGIN_ENDPOINT, loginFormBody, { headers: headers })
            .pipe(
                tap((data: any) => {

                    // Datos obtenidos
                    console.log('Datos obtenidos', data);

                    if (data.access_token != null) {
                        // Guardado del access token en el storage
                        this._storage.set(TOKEN_KEY, data.access_token);
                        this.isAuthenticated.next(true);

                        // Guardado del nombre de usuario en el storage
                        this.setUsernameStorage(credentials.email);

                        // Guardado del tipo del usuario
                        if (data.user_type != null) {
                            this.setTypeUserStorage(data.user_type);
                            this.userType.next(data.user_type);
                        }
                    } else {
                        this.isAuthenticated.next(false);
                    }

                    console.log('Validado como ', data.user_type);
                })
            );
    }

    /**
     * Logs out the user by setting the isAuthenticated flag to false and removing the token from storage.
     *
     * @returns A promise that resolves when the logout process is complete.
     */
    public async logout(): Promise<void> {
        this.isAuthenticated.next(false);
        this.userType.next(null);

        // Se comprueba si el storage está inicializado
        if (this._storage == null) {
            await this.storage.create()
                .then((storage) => {
                    this._storage = storage;

                    // Se elimina el token
                    return this._storage.remove(TOKEN_KEY);
                });
        } else {
            // Se elimina el token
            return this._storage.remove(TOKEN_KEY);
        }
    }

    /**
     * Sets the username in the storage.
     *
     * @param username - The username to be stored.
     * @returns - A promise that resolves when the username is successfully stored.
     */
    public async setUsernameStorage(username: string) {
        await this._storage.remove(USERNAME_TOKEN_KEY);
        await this._storage.set(USERNAME_TOKEN_KEY, username);
    }

    /**
     * Retrieves the type user from storage.
     *
     * @returns The type user if it exists in storage, otherwise an empty string.
     */
    public async getTypeUser(): Promise<string> {
        const typeUser = await this._storage.get(TYPE_USER_KEY);
        return typeUser || '';
    }

    /**
     * Retrieves the type user from storage.
     *
     * @returns The type user if it exists in storage, otherwise an empty string.
     */
    public getTypeUserStorage(): Observable<string> {
        return from(this._storage.get(TYPE_USER_KEY)).pipe(
            switchMap(token => {
                if (token && token.value) {
                    return of(token.value);
                } else {
                    return of('');
                }
            })
        );
    }

    /**
     * Sets the type user in the storage.
     *
     * @param typeUser - The type user to be stored.
     * @returns - A promise that resolves when the type user is successfully stored.
     */
    public async setTypeUserStorage(typeUser: string) {
        await this._storage.remove(TYPE_USER_KEY);
        await this._storage.set(TYPE_USER_KEY, typeUser);
    }

    /**
     * Retrieves the username from storage.
     *
     * @returns The username if it exists in storage, otherwise an empty string.
     */
    public async getUsernameStorage(): Promise<string> {

        // Comprobar que se ha inicializado el storage
        if (this._storage == null) {
            await this.storage.create()
                .then((storage) => {
                    this._storage = storage;

                    return this.devolverUsuario();
                });
        } else {
            return this.devolverUsuario();
        }
    }

    private async devolverUsuario(): Promise<string> {
        const token = await this._storage.get(USERNAME_TOKEN_KEY);
        if (token && token !== '') {
            return token;
        } else {
            return '';
        }
    }

    public loadActivities(): Observable<any[]> {
        const cargarToken = from(this.loadToken());

        return new Observable<any[]>((observer) => {
            cargarToken
                .pipe(
                    concatMap(() => {
                        const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + this.token });
                        return this.http.get(this.apiUrl + ACTIVITIES_ENDPOINT, { headers: headers });
                    })
                )
                .subscribe({
                    next: (resultado: HttpResponseAPI) => {
                        observer.next(resultado.data);
                    },
                    complete: () => {
                        observer.complete();
                    }
                });
        });
    }

}
